import { Grid, makeStyles } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentCategory, DocumentDetailOptionResponse, DocumentLevel } from '../../../api';
import { FileSelectButton } from '../../../shared/Components/FileSelectButton';
import PIMFormikSelect from '../../../shared/Components/PIMFormikSelect';
import PIMFormikTextField from '../../../shared/Components/PIMFormikTextField';
import PIMFormikToggle from '../../../shared/Components/PIMFormikToggle';
import PIMIconButton from '../../../shared/Components/PIMIconButton';
import { Document, DropdownOptions, PropertyApplyForAll } from './DocumentsUpload';

interface IProps {
  documents: Document[];
  applyForAll: PropertyApplyForAll;
  details: DocumentDetailOptionResponse[];
  fieldArrayPrefix: string;
  options: DropdownOptions;
  deleteDocument: (docuSment: Document) => void;
  addDocuments: (files: File[]) => void;
}

export function DocumentsUploadGrid(props: IProps): JSX.Element {
  const { t } = useTranslation();
  const classes = useStyles();
  const { documents, details, applyForAll, fieldArrayPrefix, options, deleteDocument, addDocuments } = props;

  const newDocuments = documents.filter((document) => document.isNew);
  const existingDocuments = documents.filter((document) => !document.isNew);
  const firstDocumentPrefix = `${fieldArrayPrefix}.${documents.indexOf(newDocuments[0])}.`;

  return (
    <>
      <Grid container item xs={6} justifyContent={'flex-start'}>
        <div className={classes.SubTitle}>{t('Neue Dokumente hinzufügen')}</div>
      </Grid>
      <Grid container item xs={6} justifyContent={'flex-end'}>
        <FileSelectButton multipleFiles onFilesSelected={addDocuments} color='secondary' text={t('Hinzufügen')} />
      </Grid>
      <Grid container item xs={12} className={classes.DocumentItem}>
        {newDocuments.length > 0 && (
          <DocumentEntry
            fieldPrefix={firstDocumentPrefix}
            document={newDocuments[0]}
            details={details}
            deleteDocument={deleteDocument}
            editable
            options={options}
          />
        )}
      </Grid>
      {newDocuments.length > 1 && (
        <>
          <Grid container item xs={12} className={classes.Row2} spacing={2}>
            <Grid item className={classes.Filename}>
              <div className={classes.SubSubTitle}>{t('Angaben übernehmen')}</div>
            </Grid>
            <Grid item className={classes.Select}>
              <PIMFormikToggle name={'applyForAll.documentCategory'} />
            </Grid>
            <Grid item className={classes.Select}>
              <PIMFormikToggle name={'applyForAll.countryCode'} />
            </Grid>
            <Grid item className={classes.Select}>
              <PIMFormikToggle name={'applyForAll.documentLevel'} />
            </Grid>
            <Grid item className={classes.Select}>
              <PIMFormikToggle name={'applyForAll.documentDetail'} />
            </Grid>
            <Grid item className={classes.ToggleExport} />
            <Grid item className={classes.ToggleExportKP} />
            <Grid item className={classes.Delete} />
          </Grid>
          <Grid container item xs={12} className={classes.DocumentItem}>
            {newDocuments.slice(1).map((document, index) => (
              <DocumentEntry
                key={index}
                document={document}
                applyForAll={applyForAll}
                fieldPrefix={`${fieldArrayPrefix}.${documents.indexOf(document)}.`}
                firstDocumentPrefix={firstDocumentPrefix}
                details={details}
                deleteDocument={deleteDocument}
                editable
                options={options}
              />
            ))}
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <div className={classes.SubTitle}>{t('Bestehende Dokumente updaten')}</div>
      </Grid>
      {existingDocuments.length > 0 && (
        <Grid container item xs={12} className={classes.DocumentItem}>
          {existingDocuments.map((document, index) => (
            <DocumentEntry
              key={index}
              document={document}
              fieldPrefix={`${fieldArrayPrefix}.${documents.indexOf(document)}.`}
              details={details}
              deleteDocument={deleteDocument}
              options={options}
            />
          ))}
        </Grid>
      )}
    </>
  );
}

interface DocumentItemProps {
  document: Document;
  details: DocumentDetailOptionResponse[];
  fieldPrefix: string;
  firstDocumentPrefix?: string;
  applyForAll?: PropertyApplyForAll;
  editable?: boolean;
  options: DropdownOptions;
  deleteDocument: (document: Document) => void;
}

export function DocumentEntry(props: DocumentItemProps): JSX.Element {
  const { t } = useTranslation();
  const classes = useStyles();
  const { details, fieldPrefix, applyForAll, editable, deleteDocument, document, firstDocumentPrefix, options } = props;
  const [documentLevel, setDocumentLevel] = useState(document.documentLevel);
  const [documentDetail, setDocumentDetail] = useState<DocumentDetailOptionResponse | undefined>(
    details.find((d) => d.detailName === document.detailName)
  );

  useEffect(() => {
    setDocumentDetail(details.find((d) => d.detailName === document.detailName));
    setDocumentLevel(document.documentLevel);
  }, [document, details]);

  const detailOptions =
    editable && documentLevel !== undefined
      ? options.documentDetailOptions.filter((d) => d.documentLevel === documentLevel)
      : options.documentDetailOptions;

  const onChangeDocumentLevel = (newDocumentLevel: DocumentLevel) => {
    if (documentLevel !== newDocumentLevel) {
      document.detailName = document.documentLevel === newDocumentLevel ? document.detailName : '';
      document.documentLevel = newDocumentLevel;
    }
    setDocumentLevel(newDocumentLevel);
  };

  const onChangeDocumentDetail = (detailName: string) => {
    const newDetail = details.find((d) => d.detailName === detailName);
    if (documentDetail?.detailName !== detailName && newDetail !== null && newDetail !== undefined) {
      document.documentLevel = newDetail.documentLevel;
      document.detailName = newDetail.detailName;
    }
    setDocumentDetail(newDetail);
  };

  const categoryFieldName = `${
    applyForAll?.documentCategory ? firstDocumentPrefix ?? '' : fieldPrefix
  }documentCategory`;
  const countryCodeFieldName = `${applyForAll?.countryCode ? firstDocumentPrefix ?? '' : fieldPrefix}countryCode`;
  const documentLevelFieldName = `${applyForAll?.documentLevel ? firstDocumentPrefix ?? '' : fieldPrefix}documentLevel`;
  const documentDetailFieldName = `${applyForAll?.documentDetail ? firstDocumentPrefix ?? '' : fieldPrefix}detailName`;

  return (
    <Grid container item xs={12} className={classes.Row} spacing={2}>
      <Grid item className={classes.Filename}>
        <PIMFormikTextField name={fieldPrefix + 'fileName'} label={t('Dateiname')} disabled fullWidth />
      </Grid>
      <Grid item className={classes.Select}>
        <PIMFormikSelect
          name={categoryFieldName}
          label={t('Typ')}
          required
          disabled={applyForAll?.documentCategory} // category can be edited also for existing documents
          options={options.documentCategoryOptions}
          formControlProps={{ fullWidth: true }}
        />
      </Grid>
      <Grid item className={classes.Select}>
        <PIMFormikSelect
          name={countryCodeFieldName}
          label={t('Land')}
          required
          disabled={!editable || applyForAll?.countryCode}
          hideRemoveSelection
          options={options.countryTypeOptions}
          formControlProps={{ fullWidth: true }}
        />
      </Grid>
      <Grid item className={classes.Select}>
        <PIMFormikSelect
          name={documentLevelFieldName}
          label={t('Ebene')}
          required
          disabled={!editable || applyForAll?.documentLevel}
          hideRemoveSelection
          options={options.documentLevelOptions}
          onChange={(event) => onChangeDocumentLevel(event.target.value as DocumentLevel)}
          formControlProps={{ fullWidth: true }}
        />
      </Grid>
      <Grid item className={classes.Select}>
        <PIMFormikSelect
          name={documentDetailFieldName}
          label={t('Detail')}
          required
          disabled={!editable || applyForAll?.documentDetail}
          hideRemoveSelection
          options={detailOptions}
          formControlProps={{ fullWidth: true }}
          onChange={(event) => onChangeDocumentDetail(event.target.value as string)}
        />
      </Grid>
      <Grid item className={classes.ToggleExport}>
        <PIMFormikToggle
          label={t('Für Export')}
          name={fieldPrefix + 'isExported'}
          disabled={!editable || document.documentCategory === DocumentCategory.ExternalLayout}
        />
      </Grid>
      <Grid item className={classes.ToggleExportKP}>
        <PIMFormikToggle
          label={t('Export zum Kundenportal')}
          name={fieldPrefix + 'isKpExported'}
          disabled={!editable}
        />
      </Grid>
      <Grid item className={classes.Delete}>
        <PIMIconButton size='small' onClick={() => deleteDocument(document)} iconName='trash' />
      </Grid>
    </Grid>
  );
}

const useStyles = makeStyles(() => ({
  SubTitle: {
    fontSize: '20px;',
    alignItems: 'center',
    lineHeight: '120%',
    display: 'flex',
  },
  SubSubTitle: {
    fontSize: '16px;',
    alignItems: 'center',
    lineHeight: '120%',
    display: 'flex',
  },
  PanelButtons: {
    marginLeft: '24px',
    justifyContent: 'flex-end',
  },
  Select: {
    width: '15%',
    minWidth: '250px',
    position: 'relative',
  },
  formControlWithError: {
    '& .MuiFormHelperText-root': {
      position: 'absolute',
      bottom: '-20px',
      left: 0,
      margin: 0,
    },
  },
  Filename: { width: '20%', minWidth: '300px' },

  DocumentItem: {
    width: '100%',
  },
  ToggleExport: {
    width: '7%',
    minWidth: '130px',
  },
  ToggleExportKP: {
    width: '7%',
    minWidth: '180px',
  },
  Delete: { width: '6%', textAlignLast: 'right' },
  Row: {
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'nowrap',
    flexGrow: 1,
    width: '100%',
    minHeight: '60px',
  },
  Row2: {
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'nowrap',
    width: '100%',
  },
}));
